import { TableRowSelection } from "antd/lib/table/interface";
import { useState, useMemo, Key } from "react";

interface UseTableSelectionProps {
  initialSelected?: Key[];
}

export interface UseTableSelectionReturn {
  // Estado
  selectedKeys: Key[];

  // Setters
  setSelectedKeys: (keys: Key[]) => void;

  // Funciones de selección
  toggleSelection: (key: Key) => void;
  selectKey: (key: Key) => void;
  unselectKey: (key: Key) => void;
  selectKeys: (keys: Key[]) => void;
  unselectKeys: (keys: Key[]) => void;
  selectAll: (availableKeys: Key[]) => void;
  unselectAll: () => void;

  // Funciones de utilidad
  isSelected: (key: Key) => boolean;
  anySelected: boolean;
  countSelected: number;
  resetSelected: () => void;

  // Configuración para Antd
  rowSelection: TableRowSelection;
}

export const useTableSelection = ({
  initialSelected = [],
}: UseTableSelectionProps = {}): UseTableSelectionReturn => {
  const [selectedKeys, setSelectedKeys] = useState<Key[]>(initialSelected);

  // Funciones de selección individual
  const toggleSelection = (key: Key): void => {
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const selectKey = (key: Key): void => {
    if (!selectedKeys.includes(key)) {
      setSelectedKeys((prev) => [...prev, key]);
    }
  };

  const unselectKey = (key: Key): void => {
    setSelectedKeys((prev) => prev.filter((k) => k !== key));
  };

  // Funciones de selección múltiple
  const selectKeys = (keys: Key[]): void => {
    setSelectedKeys((prev) => {
      const newKeys = keys.filter((key) => !prev.includes(key));
      return [...prev, ...newKeys];
    });
  };

  const unselectKeys = (keys: Key[]): void => {
    setSelectedKeys((prev) => prev.filter((key) => !keys.includes(key)));
  };

  const selectAll = (availableKeys: Key[]): void => {
    setSelectedKeys([...availableKeys]);
  };

  const unselectAll = (): void => {
    setSelectedKeys([]);
  };

  // Funciones de consulta
  const isSelected = (key: Key): boolean => {
    return selectedKeys.includes(key);
  };

  const anySelected = useMemo(() => selectedKeys.length > 0, [selectedKeys]);
  const countSelected = selectedKeys.length;

  const resetSelected = (): void => {
    setSelectedKeys([]);
  };

  // Configuración para Antd Table
  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (selectedRowKeys: Key[]) => {
      setSelectedKeys(selectedRowKeys);
    },
    preserveSelectedRowKeys: true,
    columnWidth: "2rem",
  };

  return {
    // Estado
    selectedKeys,

    // Setters
    setSelectedKeys,

    // Funciones de selección
    toggleSelection,
    selectKey,
    unselectKey,
    selectKeys,
    unselectKeys,
    selectAll,
    unselectAll,

    // Funciones de utilidad
    isSelected,
    anySelected,
    countSelected,
    resetSelected,

    // Configuración para Antd
    rowSelection,
  };
};
